/**
 * EOD Form Builder Library
 *
 * Defines question types, audiences, and template questions for EOD forms.
 * Hard-coded template questions match the fields required by the EOD dashboard pages.
 */

export const EOD_FORM_AUDIENCES = ["setter", "closer"] as const;
export const EOD_QUESTION_TYPES = [
  "short_text",
  "long_text",
  "number",
  "currency",
  "yes_no",
  "rating",
  "select",
  "date",
] as const;

export type EodFormAudience = (typeof EOD_FORM_AUDIENCES)[number];
export type EodQuestionType = (typeof EOD_QUESTION_TYPES)[number];
export type EodQuestionSource = "template" | "custom";

export interface EodFormQuestion {
  id: string;
  key: string;
  label: string;
  description: string;
  placeholder: string;
  section: string;
  type: EodQuestionType;
  required: boolean;
  options: string[];
  scaleMax: number;
  source: EodQuestionSource;
}

export interface EodFormTemplate {
  audience: EodFormAudience;
  title: string;
  description: string;
  questions: EodFormQuestion[];
}

export interface EodFormTemplateRecord {
  setter: EodFormTemplate;
  closer: EodFormTemplate;
}

export const QUESTION_TYPE_LABELS: Record<EodQuestionType, string> = {
  short_text: "Short answer",
  long_text: "Long answer",
  number: "Number",
  currency: "Currency",
  yes_no: "Yes / No",
  rating: "Rating",
  select: "Single select",
  date: "Date",
};

export const AUDIENCE_LABELS: Record<EodFormAudience, string> = {
  setter: "Setter",
  closer: "Closer",
};

const createId = (prefix: string) => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

export function slugifyQuestionKey(value: string) {
  const normalized = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return normalized || `question_${Math.random().toString(36).slice(2, 7)}`;
}

function createQuestion(question: Omit<EodFormQuestion, "id"> & { id?: string }): EodFormQuestion {
  return {
    id: question.id || createId("question"),
    key: question.key,
    label: question.label,
    description: question.description,
    placeholder: question.placeholder,
    section: question.section,
    type: question.type,
    required: question.required,
    options: [...question.options],
    scaleMax: question.scaleMax,
    source: question.source,
  };
}

/**
 * Setter base questions - REQUIRED for EOD dashboard to work correctly
 * These fields map to ISetterEOD in analytics.ts
 */
function setterBaseQuestions(): EodFormQuestion[] {
  return [
    createQuestion({
      key: "eodDate",
      label: "EOD date",
      description: "Which day does this report cover?",
      placeholder: "",
      section: "Daily Snapshot",
      type: "date",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "setterName",
      label: "Setter name",
      description: "Who is submitting this report?",
      placeholder: "Enter your name",
      section: "Daily Snapshot",
      type: "short_text",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "dialType",
      label: "Dial type",
      description: "What list did you dial today?",
      placeholder: "",
      section: "Daily Snapshot",
      type: "select",
      required: true,
      options: ["Applications", "Opt-ins"],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "dials",
      label: "Dials",
      description: "How many outbound dials did you complete?",
      placeholder: "0",
      section: "Volume",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "connected",
      label: "Connected",
      description: "How many real conversations were made?",
      placeholder: "0",
      section: "Volume",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "leadsBooked",
      label: "Leads booked",
      description: "How many appointments were booked?",
      placeholder: "0",
      section: "Pipeline",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "qualifiedLeads",
      label: "Qualified leads",
      description: "How many booked leads were qualified?",
      placeholder: "0",
      section: "Pipeline",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "leadflowConfidence",
      label: "Leadflow confidence",
      description: "How confident are you in the quality of leadflow?",
      placeholder: "",
      section: "Pipeline",
      type: "rating",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "wentWell",
      label: "What went well today?",
      description: "Capture the wins worth repeating.",
      placeholder: "What should the team keep doing?",
      section: "Coaching",
      type: "long_text",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "couldImprove",
      label: "What needs improvement?",
      description: "Highlight the biggest friction point from the day.",
      placeholder: "What should change tomorrow?",
      section: "Coaching",
      type: "long_text",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "cashBlockEffective",
      label: "Was the cash block effective?",
      description: "Track whether the money objection handling landed well.",
      placeholder: "",
      section: "Compliance",
      type: "yes_no",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "calendarAdherence",
      label: "Did you follow the calendar?",
      description: "Check if the expected schedule was followed.",
      placeholder: "",
      section: "Compliance",
      type: "yes_no",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "cashUpdated",
      label: "Was cash tracking updated?",
      description: "Confirm whether all cash activity was logged.",
      placeholder: "",
      section: "Compliance",
      type: "yes_no",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "needHelp",
      label: "What support is needed?",
      description: "Give managers a concrete ask before tomorrow starts.",
      placeholder: "What blocker or support request should leadership know about?",
      section: "Support",
      type: "long_text",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
  ];
}

/**
 * Closer base questions - REQUIRED for EOD dashboard to work correctly
 * These fields map to ICloserEOD in analytics.ts
 */
function closerBaseQuestions(): EodFormQuestion[] {
  return [
    createQuestion({
      key: "eodDate",
      label: "EOD date",
      description: "Which day does this report cover?",
      placeholder: "",
      section: "Daily Snapshot",
      type: "date",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "closerName",
      label: "Closer name",
      description: "Who is submitting this report?",
      placeholder: "Enter your name",
      section: "Daily Snapshot",
      type: "short_text",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "callsOnCalendar",
      label: "Calls on calendar",
      description: "How many appointments were scheduled?",
      placeholder: "0",
      section: "Performance",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "showedUp",
      label: "Showed up",
      description: "How many scheduled calls actually showed?",
      placeholder: "0",
      section: "Performance",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "offersMade",
      label: "Offers made",
      description: "How many offers were presented?",
      placeholder: "0",
      section: "Performance",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "noShows",
      label: "No-shows",
      description: "How many scheduled prospects did not attend?",
      placeholder: "0",
      section: "Performance",
      type: "number",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "reschedules",
      label: "Reschedules",
      description: "How many calls were pushed to a later date?",
      placeholder: "0",
      section: "Performance",
      type: "number",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "closes",
      label: "Closes",
      description: "How many deals were closed today?",
      placeholder: "0",
      section: "Revenue",
      type: "number",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "cashGenerated",
      label: "Cash generated",
      description: "How much new cash was collected?",
      placeholder: "0",
      section: "Revenue",
      type: "currency",
      required: true,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "leadflowConfidence",
      label: "Leadflow confidence",
      description: "How confident are you in call quality and pipeline health?",
      placeholder: "",
      section: "Revenue",
      type: "rating",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "wentWell",
      label: "What went well today?",
      description: "Capture the wins worth repeating.",
      placeholder: "What should the team keep doing?",
      section: "Coaching",
      type: "long_text",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "couldImprove",
      label: "What needs improvement?",
      description: "Highlight the biggest friction point from the day.",
      placeholder: "What should change tomorrow?",
      section: "Coaching",
      type: "long_text",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "cashBlockEffective",
      label: "Was the cash block effective?",
      description: "Track whether the money objection handling landed well.",
      placeholder: "",
      section: "Compliance",
      type: "yes_no",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "mrrsUpToDate",
      label: "Are MRRs up to date?",
      description: "Confirm subscription and recurring revenue tracking is current.",
      placeholder: "",
      section: "Compliance",
      type: "yes_no",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "calendarAdherence",
      label: "Did you follow the calendar?",
      description: "Check if the expected schedule was followed.",
      placeholder: "",
      section: "Compliance",
      type: "yes_no",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
    createQuestion({
      key: "needHelp",
      label: "What support is needed?",
      description: "Give managers a concrete ask before tomorrow starts.",
      placeholder: "What blocker or support request should leadership know about?",
      section: "Support",
      type: "long_text",
      required: false,
      options: [],
      scaleMax: 5,
      source: "template",
    }),
  ];
}

const BASE_TEMPLATES: EodFormTemplateRecord = {
  setter: {
    audience: "setter",
    title: "Setter End of Day",
    description: "Daily volume, pipeline quality, and coaching prompts for appointment setters.",
    questions: setterBaseQuestions(),
  },
  closer: {
    audience: "closer",
    title: "Closer End of Day",
    description: "Daily show, close, cash, and coaching prompts for closers.",
    questions: closerBaseQuestions(),
  },
};

export function cloneQuestion(question: EodFormQuestion): EodFormQuestion {
  return createQuestion(question);
}

export function cloneTemplate(template: EodFormTemplate): EodFormTemplate {
  return {
    audience: template.audience,
    title: template.title,
    description: template.description,
    questions: template.questions.map(cloneQuestion),
  };
}

export function createBaseEodTemplate(audience: EodFormAudience): EodFormTemplate {
  return cloneTemplate(BASE_TEMPLATES[audience]);
}

export function createBaseEodTemplateRecord(): EodFormTemplateRecord {
  return {
    setter: createBaseEodTemplate("setter"),
    closer: createBaseEodTemplate("closer"),
  };
}

export function createBlankQuestion(audience: EodFormAudience, type: EodQuestionType = "short_text"): EodFormQuestion {
  const noun = audience === "setter" ? "setter" : "closer";
  const label = `New ${noun} question`;

  return createQuestion({
    key: slugifyQuestionKey(label),
    label,
    description: "",
    placeholder: type === "long_text" ? "Type your prompt" : "",
    section: "Custom Questions",
    type,
    required: false,
    options: type === "select" ? ["Option 1", "Option 2"] : [],
    scaleMax: 5,
    source: "custom",
  });
}

export function createBlankEodTemplate(audience: EodFormAudience): EodFormTemplate {
  return {
    audience,
    title: `${AUDIENCE_LABELS[audience]} End of Day`,
    description: `Build a custom ${AUDIENCE_LABELS[audience].toLowerCase()} report from scratch.`,
    questions: [],
  };
}

export function sanitizeQuestion(
  audience: EodFormAudience,
  question: Partial<EodFormQuestion> | null | undefined,
  index: number,
): EodFormQuestion {
  const fallback = createBlankQuestion(audience);
  const label = typeof question?.label === "string" && question.label.trim()
    ? question.label.trim()
    : fallback.label;
  const type = EOD_QUESTION_TYPES.includes(question?.type as EodQuestionType)
    ? (question?.type as EodQuestionType)
    : fallback.type;
  // Preserve existing keys as-is (e.g. camelCase "setterName").
  // Only slugify when generating a NEW key from a label.
  const key = typeof question?.key === "string" && question.key.trim()
    ? question.key.trim()
    : slugifyQuestionKey(label || `question_${index + 1}`);

  return {
    id: typeof question?.id === "string" && question.id.trim() ? question.id : createId("question"),
    key,
    label,
    description: typeof question?.description === "string" ? question.description.trim() : "",
    placeholder: typeof question?.placeholder === "string" ? question.placeholder : "",
    section: typeof question?.section === "string" && question.section.trim() ? question.section.trim() : fallback.section,
    type,
    required: Boolean(question?.required),
    options: Array.isArray(question?.options)
      ? question.options.map((option) => `${option}`.trim()).filter(Boolean)
      : type === "select"
        ? ["Option 1", "Option 2"]
        : [],
    scaleMax: typeof question?.scaleMax === "number" && Number.isFinite(question.scaleMax)
      ? Math.max(3, Math.min(10, Math.round(question.scaleMax)))
      : 5,
    source: question?.source === "template" ? "template" : "custom",
  };
}

function normalizeComparable(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function isBaseQuestionMatch(base: EodFormQuestion, candidate: EodFormQuestion): boolean {
  if (candidate.key === base.key) return true;
  if (candidate.source === "template" && normalizeComparable(candidate.label) === normalizeComparable(base.label)) {
    return true;
  }
  return false;
}

function mergeWithBaseQuestions(
  audience: EodFormAudience,
  questions: EodFormQuestion[],
): EodFormQuestion[] {
  const baseQuestions = createBaseEodTemplate(audience).questions;
  const usedIndexes = new Set<number>();

  const mergedBaseQuestions = baseQuestions.map((baseQuestion) => {
    const matchIndex = questions.findIndex((question, index) => (
      !usedIndexes.has(index) && isBaseQuestionMatch(baseQuestion, question)
    ));

    if (matchIndex === -1) {
      return cloneQuestion(baseQuestion);
    }

    usedIndexes.add(matchIndex);
    const existing = questions[matchIndex]!;

    return {
      ...existing,
      key: baseQuestion.key,
      type: baseQuestion.type,
      source: "template",
      required: baseQuestion.required || existing.required,
      options: baseQuestion.type === "select"
        ? (existing.options.length > 0 ? existing.options : [...baseQuestion.options])
        : [...baseQuestion.options],
      scaleMax: baseQuestion.type === "rating" ? existing.scaleMax : baseQuestion.scaleMax,
    };
  });

  const customQuestions = questions
    .filter((question, index) => !usedIndexes.has(index))
    .filter((question) => question.source === "custom");

  return [...mergedBaseQuestions, ...customQuestions];
}

export function sanitizeTemplate(
  audience: EodFormAudience,
  template: Partial<EodFormTemplate> | null | undefined,
): EodFormTemplate {
  const fallback = createBaseEodTemplate(audience);
  const title = typeof template?.title === "string" && template.title.trim()
    ? template.title.trim()
    : fallback.title;
  const description = typeof template?.description === "string"
    ? template.description.trim()
    : fallback.description;
  const sanitizedQuestions = Array.isArray(template?.questions) && template.questions.length > 0
    ? template.questions.map((question, index) => sanitizeQuestion(audience, question, index))
    : fallback.questions.map(cloneQuestion);
  const questions = mergeWithBaseQuestions(audience, sanitizedQuestions);

  return {
    audience,
    title,
    description,
    questions,
  };
}

/**
 * Get the Google Sheets column headers for EOD submissions
 * Used when creating the "Eods" sheet
 */
export function getEodSheetHeaders(audience: EodFormAudience): string[] {
  const baseHeaders = ["Timestamp", "Form Title"];
  const template = createBaseEodTemplate(audience);
  const questionHeaders = template.questions.map(q => q.label);
  return [...baseHeaders, ...questionHeaders];
}

export function normalizeSubmissionAnswers(
  audience: EodFormAudience,
  answers: Record<string, unknown>,
  template: Partial<EodFormTemplate> | null | undefined,
): Record<string, unknown> {
  const normalizedAnswers: Record<string, unknown> = { ...answers };
  const baseQuestions = createBaseEodTemplate(audience).questions;

  // Build a case-insensitive lookup of submitted answer keys for robust matching.
  // This handles legacy slugified keys (e.g. "settername") matching canonical ("setterName").
  const answersLower = new Map<string, { originalKey: string; value: unknown }>();
  for (const [k, v] of Object.entries(answers)) {
    answersLower.set(k.toLowerCase(), { originalKey: k, value: v });
  }

  // Template-based matching (original logic)
  const templateQuestions = Array.isArray(template?.questions)
    ? template.questions.map((question, index) => sanitizeQuestion(audience, question, index))
    : [];

  for (const templateQuestion of templateQuestions) {
    const baseMatch = baseQuestions.find((baseQuestion) => isBaseQuestionMatch(baseQuestion, templateQuestion));
    if (!baseMatch || templateQuestion.key === baseMatch.key) continue;

    const existingCanonicalValue = normalizedAnswers[baseMatch.key];
    if (existingCanonicalValue !== undefined && existingCanonicalValue !== null && existingCanonicalValue !== "") {
      continue;
    }

    const submittedValue = answers[templateQuestion.key];
    if (submittedValue !== undefined) {
      normalizedAnswers[baseMatch.key] = submittedValue;
    }
  }

  // Fallback: case-insensitive key matching directly against base question keys.
  // Catches cases where template matching fails (e.g. label was customized).
  for (const baseQuestion of baseQuestions) {
    const existing = normalizedAnswers[baseQuestion.key];
    if (existing !== undefined && existing !== null && existing !== "") continue;

    const lowerMatch = answersLower.get(baseQuestion.key.toLowerCase());
    if (lowerMatch && lowerMatch.value !== undefined && lowerMatch.value !== null && lowerMatch.value !== "") {
      normalizedAnswers[baseQuestion.key] = lowerMatch.value;
    }
  }

  return normalizedAnswers;
}
